<template>
  <div>
    <a-form-item label="eSIM商品">
      <a-select
        :loading="loadingEsimProductOptions"
        @change="handleEsimProductChange"
        show-search
        :filter-option="filterOption"
        v-decorator="[
          'agents_product_id',
          { rules: [{ required: true, message: '请选择eSIM商品' }] },
        ]"
        placeholder="请选择客户已上架eSIM"
      >
        <a-select-option
          v-for="record in esimProductOptions"
          :key="record.id"
          :value="record.id"
        >
          {{ record.sgp_spec_names ? `${record.name}（${record.sgp_spec_names}）` : record.name }}
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="卡片材质">
      <a-select
        :loading="loadingCardTypes"
        @change="handleCardTypeChange"
        show-search
        :filter-option="filterOption"
        :disabled="!selectedEsimProduct"
        v-decorator="[
          'card_type_id',
          { rules: [{ required: true, message: '请选择卡片材质' }] },
        ]"
        placeholder="请选择卡片材质"
      >
        <a-select-option
          v-for="record in cardTypeOptions"
          :key="record.card_type_id"
          :value="record.card_type_id"
        >
          {{ record.card_type_name }}（{{ record.price }} 元/张）
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="卡片尺寸">
      <a-select
        :loading="loadingCardShapes"
        show-search
        :filter-option="filterOption"
        :disabled="!selectedCardTypeId"
        v-decorator="[
          'card_shape_id',
          { rules: [{ required: true, message: '请选择卡片尺寸' }] },
        ]"
        placeholder="请选择卡片尺寸"
      >
        <a-select-option
          v-for="record in cardShapeOptions"
          :key="record.id"
          :value="record.id"
        >
          {{ record.name }}
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="单价(元)">
      <a-input
        :value="unitPriceDisplay"
        disabled
      />
    </a-form-item>

    <a-form-item label="数量" required="required">
      <a-space>
        <a-input-number
          :min="1"
          :max="productCountMaxValue"
          :step="1"
          :precision="0"
          style="width: 120px"
          :formatter="parseProductCountInputValue"
          :parser="parseProductCountInputValue"
          @change="handleProductCountChange"
          v-decorator="['product_count', {
            rules: [
              { required: true, message: '请输入采购数量' }
            ]
          }]"
        />
        <span>张</span>
      </a-space>
    </a-form-item>

    <a-form-item label="产品备注">
      <a-textarea
        v-decorator="['product_remark', {
          rules: [
            { max: 200, message: '最多200个字符' },
          ]
        }]"
        :auto-size="{ minRows: 2, maxRows: 5 }"
      />
    </a-form-item>

    <a-descriptions
      :column="1"
      class="iotplt-descriptions iotplt-descriptions-max-6"
    >
      <a-descriptions-item label="费用(元)">
        {{ productFeeDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="小计(元)">
        {{ totalFeeDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="账户余额(元)">
        {{ balanceDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="可用额度(元)">
        {{ availableDisplay }}
      </a-descriptions-item>

      <a-descriptions-item label="已授信(元)">
        {{ creditDisplay }}
      </a-descriptions-item>
    </a-descriptions>

    <a-form-item label="新增授信(元)">
      <a-input-number
        :min="0"
        :max="10000000"
        :step="0.01"
        :precision="2"
        style="width: 100%;"
        v-decorator="['credit', {
          rules: [
            { required: true, message: '请输入授信额度' }
          ]
        }]"
      />
    </a-form-item>
  </div>
</template>

<script>
import { formatFloat } from '@/utils/filter'
import { findAgentsEsimProducts, findAgentsEsimProductForm } from '@/api/agents_esim_product'
import { findCardShapeOptions } from '@/api/common_options'
import { findPreOrderItemLastRemarks } from '@/api/agent_pre_order_item'

export default {
  name: 'NewEsimPreOrderProductForm',
  props: {
    form: {
      type: Object,
      required: true
    },
    agentId: {
      type: Number,
      required: true
    },
    productTypeId: {
      type: Number,
      required: true
    },
    financeOverview: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    this.calculateProductFee = this.$lodash.debounce(this.calculateProductFee, 400)
    return {
      esimProductOptions: [],
      loadingEsimProductOptions: false,
      selectedEsimProduct: null,

      cardTypeOptions: [],
      loadingCardTypes: false,
      selectedCardTypeId: 0,

      cardShapeOptions: [],
      loadingCardShapes: false,

      productCountMaxValue: 10000000,
      productFeeDisplay: '',
      totalFeeDisplay: '',
      totalFeeValue: undefined
    }
  },
  computed: {
    balanceDisplay() {
      return this.formatMoney(this.financeOverview.balance)
    },
    availableDisplay() {
      return this.formatMoney(this.financeOverview.available)
    },
    creditDisplay() {
      return this.formatMoney(this.financeOverview.credit)
    },
    selectedCardType() {
      if (!this.selectedCardTypeId) {
        return null
      }
      return this.cardTypeOptions.find((item) => item.card_type_id === this.selectedCardTypeId) || null
    },
    unitPriceDisplay() {
      if (!this.selectedCardType) {
        return ''
      }
      return formatFloat(this.selectedCardType.price)
    }
  },
  watch: {
    agentId() {
      this.fetchEsimProductOptions()
    }
  },
  mounted() {
    this.fetchEsimProductOptions()
  },
  methods: {
    formatMoney(value) {
      if (value === null || value === undefined) {
        return '--'
      }
      return formatFloat(value)
    },

    fetchEsimProductOptions() {
      if (!this.agentId) {
        this.esimProductOptions = []
        return
      }
      this.loadingEsimProductOptions = true
      findAgentsEsimProducts({
        agent_id: this.agentId,
        effective: '1',
        page: 1,
        per_page: 1000
      }).then((res) => {
        if (res.code === 0) {
          this.esimProductOptions = res.data || []
        }
        this.loadingEsimProductOptions = false
      })
    },

    handleEsimProductChange(productId) {
      this.selectedEsimProduct = this.esimProductOptions.find((item) => item.id === productId) || null
      this.cardTypeOptions = []
      this.selectedCardTypeId = 0
      this.cardShapeOptions = []
      this.form.setFieldsValue({
        card_type_id: undefined,
        card_shape_id: undefined
      })
      this.clearFeeDisplay()
      if (this.selectedEsimProduct) {
        this.fetchCardTypes(this.selectedEsimProduct.id)
        this.fetchPreOrderItemLastRemarks()
      }
    },

    fetchCardTypes(productId) {
      this.loadingCardTypes = true
      findAgentsEsimProductForm(productId).then((res) => {
        if (res.code === 0) {
          this.cardTypeOptions = res.data.agents_esim_products_card_types || []
          if (this.cardTypeOptions.length > 0) {
            const first = this.cardTypeOptions[0]
            this.selectedCardTypeId = first.card_type_id
            this.form.setFieldsValue({ card_type_id: first.card_type_id })
            this.fetchCardShapes(first.card_type_id)
          }
        }
        this.loadingCardTypes = false
      }).catch(() => {
        this.loadingCardTypes = false
      })
    },

    handleCardTypeChange(cardTypeId) {
      this.selectedCardTypeId = cardTypeId
      this.cardShapeOptions = []
      this.form.setFieldsValue({ card_shape_id: undefined })
      this.clearFeeDisplay()
      if (cardTypeId) {
        this.fetchCardShapes(cardTypeId)
      }
    },

    fetchCardShapes(cardTypeId) {
      this.loadingCardShapes = true
      findCardShapeOptions(cardTypeId).then((res) => {
        if (res.code === 0) {
          this.cardShapeOptions = res.data || []
          if (this.cardShapeOptions.length > 0) {
            this.form.setFieldsValue({ card_shape_id: this.cardShapeOptions[0].id })
          }
          this.calculateProductFee()
        }
        this.loadingCardShapes = false
      }).catch(() => {
        this.loadingCardShapes = false
      })
    },

    parseProductCountInputValue(val) {
      if (!val) {
        return ''
      }

      if (val < 1) {
        val = 1
      } else if (val > this.productCountMaxValue) {
        val = this.productCountMaxValue
      }
      return parseInt(val)
    },

    handleProductCountChange(productCount) {
      this.clearFeeDisplay()
      if (productCount > 0) {
        this.calculateProductFee()
      }
    },

    calculateProductFee() {
      if (!this.selectedCardType) {
        return
      }
      this.form.validateFields(['product_count'], { force: true }, (err, values) => {
        if (!err) {
          const unitPrice = this.selectedCardType.price
          const totalFee = unitPrice * values.product_count
          this.productFeeDisplay = `${formatFloat(unitPrice)} * ${values.product_count} = ${formatFloat(totalFee)}`
          this.totalFeeDisplay = `${formatFloat(unitPrice)} * ${values.product_count} = ${formatFloat(totalFee)}`
          this.totalFeeValue = totalFee
        }
      })
    },

    clearFeeDisplay() {
      this.productFeeDisplay = ''
      this.totalFeeDisplay = ''
      this.totalFeeValue = undefined
    },

    fetchPreOrderItemLastRemarks() {
      if (!this.selectedEsimProduct) {
        return
      }
      findPreOrderItemLastRemarks({
        agent_id: this.agentId,
        product_type_id: this.productTypeId,
        product_id: this.selectedEsimProduct.id
      }).then((res) => {
        if (res.code === 0) {
          this.form.setFieldsValue({
            product_remark: res.data.product_remark,
            delivery_remark: res.data.delivery_remark,
            invoice_remark: res.data.invoice_remark
          })
        }
      })
    },

    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    }
  }
}
</script>
